import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Card, Alert, Row, Col, Form, Badge, Pagination, Modal } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaSearch, FaStore, FaUser, FaPhone, FaGlobe, FaChartLine, FaInfoCircle, FaMapMarkerAlt } from 'react-icons/fa';
import { vendorsAPI, storesAPI } from '../../services/api';
import FormModal from '../../components/Common/FormModal';
import ConfirmModal from '../../components/Common/ConfirmModal';

const Vendors = () => {
    const [vendors, setVendors] = useState([]);
    const [filteredVendors, setFilteredVendors] = useState([]);
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [storesLoading, setStoresLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showStoreDetailsModal, setShowStoreDetailsModal] = useState(false);
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [selectedStore, setSelectedStore] = useState(null);
    const [storeDetails, setStoreDetails] = useState(null);
    const [loadingStoreDetails, setLoadingStoreDetails] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    const isSubmitting = useRef(false);
    const createButtonRef = useRef(null);

    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 1
    });

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        phone: '',
        website: '',
        description: '',
        address: '',
        store_ids: []
    });

    useEffect(() => {
        fetchVendors();
    }, [pagination.page]);

    useEffect(() => {
        if (showCreateModal) {
            const initializeCreateModal = async () => {
                setStoresLoading(true);
                await fetchStores();
                setFormData({
                    name: '',
                    email: '',
                    password: '',
                    phone: '',
                    website: '',
                    description: '',
                    address: '',
                    store_ids: []
                });
                setStoresLoading(false);
            };
            initializeCreateModal();
        }
    }, [showCreateModal]);

    useEffect(() => {
        if (showEditModal && selectedVendor) {
            const initializeEditModal = async () => {
                setStoresLoading(true);
                await fetchStores();

                console.log('Setting edit form data for vendor:', selectedVendor);

                let storeIds = [];

                // Handle different possible store ID formats from the API
                if (selectedVendor.store_ids) {
                    if (Array.isArray(selectedVendor.store_ids)) {
                        storeIds = selectedVendor.store_ids;
                    } else if (typeof selectedVendor.store_ids === 'string') {
                        storeIds = selectedVendor.store_ids.split(',').map(id => id.trim()).filter(id => id !== '');
                    }
                } else if (selectedVendor.store_id) {
                    storeIds = [selectedVendor.store_id];
                } else if (selectedVendor.stores && Array.isArray(selectedVendor.stores) && selectedVendor.stores.length > 0) {
                    storeIds = selectedVendor.stores.map(store => store.id);
                }

                console.log('Extracted store IDs for vendor:', storeIds);
                console.log('Available stores:', stores.length);

                setFormData({
                    name: selectedVendor.name || '',
                    email: selectedVendor.email || '',
                    password: '',
                    phone: selectedVendor.phone || '',
                    website: selectedVendor.website || '',
                    description: selectedVendor.description || '',
                    address: selectedVendor.address || '',
                    store_ids: storeIds
                });

                setStoresLoading(false);
            };

            initializeEditModal();
        }
    }, [showEditModal, selectedVendor]);

    useEffect(() => {
        if (Array.isArray(vendors)) {
            const filtered = vendors.filter(vendor => {
                const searchLower = searchTerm.toLowerCase();
                return (
                    vendor.name?.toLowerCase().includes(searchLower) ||
                    vendor.email?.toLowerCase().includes(searchLower) ||
                    (vendor.phone && vendor.phone.toLowerCase().includes(searchLower)) ||
                    (vendor.website && vendor.website.toLowerCase().includes(searchLower)) ||
                    (vendor.address && vendor.address.toLowerCase().includes(searchLower)) ||
                    (vendor.description && vendor.description.toLowerCase().includes(searchLower))
                );
            });
            setFilteredVendors(filtered);
        }
    }, [searchTerm, vendors]);

    const fetchVendors = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await vendorsAPI.list({ page: pagination.page, limit: pagination.limit });

            console.log('Vendors API Response:', response.data);

            let vendorsData = [];
            let paginationData = {};

            // Handle the vendors API response structure from Postman collection
            if (response.data && response.data.status === 'success' && response.data.data) {
                if (response.data.data.data && Array.isArray(response.data.data.data)) {
                    vendorsData = response.data.data.data;
                } else if (Array.isArray(response.data.data)) {
                    vendorsData = response.data.data;
                }
                if (response.data.data.meta) {
                    paginationData = response.data.data.meta;
                }
            } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
                vendorsData = response.data.data;
            } else if (Array.isArray(response.data)) {
                vendorsData = response.data;
            }

            if (!Array.isArray(vendorsData)) {
                console.error('Invalid vendors data format:', response.data);
                vendorsData = [];
            }

            // Process vendors to ensure all fields are present
            const processedVendors = vendorsData.map(vendor => ({
                ...vendor,
                phone: vendor.phone || '',
                website: vendor.website || '',
                address: vendor.address || '',
                description: vendor.description || ''
            }));

            console.log('Processed vendors:', processedVendors);
            setVendors(processedVendors);
            setFilteredVendors(processedVendors);
            setPagination(prev => ({
                ...prev,
                ...paginationData,
                total: paginationData.total || processedVendors.length,
                pages: paginationData.pages || Math.ceil((paginationData.total || processedVendors.length) / prev.limit)
            }));

        } catch (err) {
            console.error('Error fetching vendors:', err);
            setError(err.response?.data?.message || 'Failed to fetch vendors');
            setVendors([]);
            setFilteredVendors([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchStores = async () => {
        try {
            setStoresLoading(true);
            const response = await storesAPI.list({ page: 1, limit: 100 });

            console.log('Stores API Response for Vendors:', response.data);

            let storesData = [];

            // Handle the stores API response structure from Postman collection
            if (response.data && response.data.status === 'success' && response.data.data) {
                // Structure: { status: "success", data: { stores: [...], meta: {...} } }
                if (response.data.data.stores && Array.isArray(response.data.data.stores)) {
                    storesData = response.data.data.stores;
                } else if (response.data.data.data && Array.isArray(response.data.data.data)) {
                    storesData = response.data.data.data;
                } else if (Array.isArray(response.data.data)) {
                    storesData = response.data.data;
                }
            } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
                storesData = response.data.data;
            } else if (Array.isArray(response.data)) {
                storesData = response.data;
            } else if (response.data && response.data.stores && Array.isArray(response.data.stores)) {
                storesData = response.data.stores;
            }

            if (!Array.isArray(storesData)) {
                console.error('Invalid stores data format:', response.data);
                storesData = [];
            }

            // Process stores to ensure we have all required fields
            const processedStores = storesData.map(store => ({
                id: store.id,
                name: store.name || 'Unnamed Store',
                code: store.code || '',
                vendor_name: store.vendor?.name || 'No Vendor',
                vendors_count: store.vendors_count || 0,
                address: store.address || '',
                city: store.city || '',
                state: store.state || '',
                postal_code: store.postal_code || store.zip_code || '',
                phone: store.phone || '',
                email: store.email || '',
                is_active: store.is_active !== undefined ? store.is_active : 1,
                created_at: store.created_at,
                updated_at: store.updated_at
            }));

            // Get unique stores by ID
            const uniqueStores = [];
            const storeIds = new Set();

            processedStores.forEach(store => {
                if (!storeIds.has(store.id)) {
                    storeIds.add(store.id);
                    uniqueStores.push(store);
                }
            });

            // Sort stores by name
            uniqueStores.sort((a, b) => a.name.localeCompare(b.name));

            console.log('Fetched stores for vendors:', uniqueStores.length);
            setStores(uniqueStores);
            return uniqueStores;
        } catch (err) {
            console.error('Error fetching stores:', err);
            setStores([]);
            return [];
        } finally {
            setStoresLoading(false);
        }
    };

    const fetchStoreDetails = async (storeId) => {
        try {
            setLoadingStoreDetails(true);
            const response = await storesAPI.getById(storeId);

            console.log('Store Details Response:', response.data);

            let storeData = null;

            if (response.data && response.data.status === 'success' && response.data.data) {
                storeData = response.data.data;
            } else if (response.data && response.data.data) {
                storeData = response.data.data;
            } else if (response.data) {
                storeData = response.data;
            }

            if (storeData) {
                // Process store data to ensure all fields are present
                const processedStore = {
                    id: storeData.id,
                    name: storeData.name || 'Unnamed Store',
                    code: storeData.code || '',
                    address: storeData.address || '',
                    city: storeData.city || '',
                    state: storeData.state || '',
                    postal_code: storeData.postal_code || storeData.zip_code || '',
                    phone: storeData.phone || '',
                    email: storeData.email || '',
                    is_active: storeData.is_active !== undefined ? storeData.is_active : 1,
                    vendors_count: storeData.vendors_count || 0,
                    vendors: storeData.vendors || [],
                    created_at: storeData.created_at,
                    updated_at: storeData.updated_at
                };

                setStoreDetails(processedStore);
            }
        } catch (err) {
            console.error('Error fetching store details:', err);
            setError('Failed to load store details');
        } finally {
            setLoadingStoreDetails(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();

        if (isSubmitting.current) {
            console.log('Create submission already in progress, preventing duplicate');
            return;
        }

        // Validate that at least one store is selected
        if (!formData.store_ids || formData.store_ids.length === 0) {
            setError('Please select at least one store for the vendor');
            return;
        }

        try {
            isSubmitting.current = true;
            setActionLoading(true);
            setError('');
            setSuccessMessage('');

            const payload = {
                name: formData.name?.trim(),
                email: formData.email?.trim(),
                password: formData.password,
                phone: formData.phone?.trim() || '',
                website: formData.website?.trim() || '',
                description: formData.description?.trim() || '',
                address: formData.address?.trim() || ''
            };

            // Include store_ids as an array (as per Postman collection)
            if (formData.store_ids && formData.store_ids.length > 0) {
                const validStoreIds = formData.store_ids.filter(id => id && id.trim() !== '');
                if (validStoreIds.length > 0) {
                    payload.store_ids = validStoreIds; // Send as array
                }
            }

            console.log('Creating vendor with payload:', payload);

            const response = await vendorsAPI.create(payload);
            console.log('Create response:', response.data);

            setSuccessMessage('Vendor created successfully!');
            setShowCreateModal(false);
            resetForm();
            await fetchVendors();

            setTimeout(() => setSuccessMessage(''), 3000);

        } catch (err) {
            console.error('Error creating vendor:', err);
            setError(err.response?.data?.message || 'Failed to create vendor');
        } finally {
            setActionLoading(false);
            setTimeout(() => {
                isSubmitting.current = false;
            }, 500);
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();

        if (isSubmitting.current) {
            console.log('Update submission already in progress, preventing duplicate');
            return;
        }

        if (!selectedVendor?.id) {
            setError('Vendor ID is missing');
            return;
        }

        // Validate that at least one store is selected
        if (!formData.store_ids || formData.store_ids.length === 0) {
            setError('Please select at least one store for the vendor');
            return;
        }

        try {
            isSubmitting.current = true;
            setActionLoading(true);
            setError('');
            setSuccessMessage('');

            const payload = {
                name: formData.name?.trim(),
                email: formData.email?.trim(),
                phone: formData.phone?.trim() || '',
                website: formData.website?.trim() || '',
                description: formData.description?.trim() || '',
                address: formData.address?.trim() || ''
            };

            // Only include password if provided
            if (formData.password) {
                payload.password = formData.password;
            }

            // Include store_ids as an array (as per Postman collection)
            if (formData.store_ids && formData.store_ids.length > 0) {
                const validStoreIds = formData.store_ids.filter(id => id && id.trim() !== '');
                if (validStoreIds.length > 0) {
                    payload.store_ids = validStoreIds; // Send as array
                }
            } else {
                payload.store_ids = [];
            }

            console.log('Updating vendor with ID:', selectedVendor.id);
            console.log('Update payload:', payload);

            const response = await vendorsAPI.update(selectedVendor.id, payload);
            console.log('Update response:', response.data);

            setSuccessMessage('Vendor updated successfully!');
            setShowEditModal(false);
            resetForm();
            setSelectedVendor(null);
            setIsEditing(false);
            await fetchVendors();

            setTimeout(() => setSuccessMessage(''), 3000);

        } catch (err) {
            console.error('Error updating vendor:', err);

            let errorMsg = 'Failed to update vendor';

            if (err.response) {
                console.error('Update Error Response:', err.response);

                if (err.response.data) {
                    if (err.response.data.message) {
                        errorMsg = err.response.data.message;
                    } else if (err.response.data.error) {
                        errorMsg = err.response.data.error;
                    }
                }

                if (err.response.status === 403) {
                    errorMsg = 'Permission denied. You cannot update this vendor.';
                } else if (err.response.status === 404) {
                    errorMsg = 'Vendor not found. It may have been deleted.';
                } else if (err.response.status === 400) {
                    errorMsg = err.response.data.message || 'Invalid vendor data. Please check your inputs.';
                } else if (err.response.status === 422) {
                    errorMsg = 'Validation error. Please check your inputs.';
                }
            } else if (err.request) {
                errorMsg = 'No response from server. Please check your connection.';
            } else {
                errorMsg = err.message || 'Failed to update vendor';
            }

            setError(errorMsg);
        } finally {
            setActionLoading(false);
            setTimeout(() => {
                isSubmitting.current = false;
            }, 500);
        }
    };

    const handleDelete = async () => {
        if (!selectedVendor?.id) {
            setError('Vendor ID is missing');
            return;
        }

        if (isSubmitting.current) {
            console.log('Delete submission already in progress, preventing duplicate');
            return;
        }

        try {
            isSubmitting.current = true;
            setActionLoading(true);
            setError('');
            setSuccessMessage('');

            console.log('Attempting to delete vendor with ID:', selectedVendor.id);

            await vendorsAPI.delete(selectedVendor.id);

            setSuccessMessage('Vendor deleted successfully!');
            setShowDeleteModal(false);
            setSelectedVendor(null);
            await fetchVendors();

            setTimeout(() => setSuccessMessage(''), 3000);

        } catch (err) {
            console.error('Error deleting vendor:', err);

            let errorMsg = 'Failed to delete vendor';

            if (err.response) {
                console.error('Delete Error Response:', err.response);

                if (err.response.data) {
                    if (err.response.data.message) {
                        errorMsg = err.response.data.message;
                    } else if (err.response.data.error) {
                        errorMsg = err.response.data.error;
                    }
                }

                if (err.response.status === 403) {
                    errorMsg = 'Permission denied. You cannot delete this vendor.';
                } else if (err.response.status === 404) {
                    errorMsg = 'Vendor not found. It may have already been deleted.';
                }
            }

            setError(errorMsg);
        } finally {
            setActionLoading(false);
            setTimeout(() => {
                isSubmitting.current = false;
            }, 500);
        }
    };

    const handleStatusChange = async (vendorId, currentStatus) => {
        if (isSubmitting.current) return;

        try {
            isSubmitting.current = true;
            const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
            await vendorsAPI.updateStatus(vendorId, newStatus);
            await fetchVendors();
        } catch (err) {
            console.error('Error changing vendor status:', err);
            setError('Failed to change vendor status');
        } finally {
            setTimeout(() => {
                isSubmitting.current = false;
            }, 500);
        }
    };

    const handleStoreCheckboxChange = (storeId) => {
        let updatedStoreIds;

        if (formData.store_ids.includes(storeId)) {
            updatedStoreIds = formData.store_ids.filter(id => id !== storeId);
        } else {
            updatedStoreIds = [...formData.store_ids, storeId];
        }

        console.log('Store checkbox toggled:', storeId, 'Selected stores:', updatedStoreIds);
        setFormData({ ...formData, store_ids: updatedStoreIds });
    };

    const selectAllStores = () => {
        const allStoreIds = stores.map(store => store.id);
        console.log('Selecting all stores:', allStoreIds);
        setFormData({ ...formData, store_ids: allStoreIds });
    };

    const clearAllStores = () => {
        console.log('Clearing all stores');
        setFormData({ ...formData, store_ids: [] });
    };

    const viewStoreDetails = async (store) => {
        console.log('Viewing store details:', store);

        // Prevent event propagation
        event?.stopPropagation();

        // First, try to find the store in our stores list
        const foundStore = stores.find(s => s.id === store.id);

        if (foundStore) {
            setSelectedStore(foundStore);
            setShowStoreDetailsModal(true);
        } else {
            // If not found in list, fetch detailed store information
            await fetchStoreDetails(store.id);
            setSelectedStore(store);
            setShowStoreDetailsModal(true);
        }
    };

    const openEditModal = (vendor) => {
        console.log('Opening edit modal for vendor:', vendor);
        setSelectedVendor(vendor);
        setIsEditing(true);
        setShowEditModal(true);
        setError('');

        setFormData({
            name: vendor.name || '',
            email: vendor.email || '',
            password: '',
            phone: vendor.phone || '',
            website: vendor.website || '',
            description: vendor.description || '',
            address: vendor.address || '',
            store_ids: []
        });
    };

    const openDeleteModal = (vendor) => {
        setSelectedVendor(vendor);
        setShowDeleteModal(true);
        setError('');
    };

    const resetForm = () => {
        setFormData({
            name: '',
            email: '',
            password: '',
            phone: '',
            website: '',
            description: '',
            address: '',
            store_ids: []
        });
        setIsEditing(false);
        setSelectedVendor(null);
        setError('');
    };

    const handleModalClose = () => {
        setShowCreateModal(false);
        setShowEditModal(false);
        resetForm();
        isSubmitting.current = false;
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.pages) {
            setPagination(prev => ({ ...prev, page }));
        }
    };

    const getStatusBadge = (status) => {
        switch (status?.toLowerCase()) {
            case 'active':
                return <Badge bg="success">Active</Badge>;
            case 'inactive':
                return <Badge bg="secondary">Inactive</Badge>;
            case 'pending':
                return <Badge bg="warning">Pending</Badge>;
            default:
                return <Badge bg="info">Active</Badge>;
        }
    };

    const getStoreNames = (vendor) => {
        if (!vendor.stores || !Array.isArray(vendor.stores) || vendor.stores.length === 0) {
            return [];
        }
        return vendor.stores.map(store => store.name);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const renderPagination = () => {
        if (pagination.pages <= 1) return null;

        const items = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, pagination.page - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(pagination.pages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        items.push(
            <Pagination.Prev
                key="prev"
                disabled={pagination.page === 1}
                onClick={() => handlePageChange(pagination.page - 1)}
            />
        );

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} active={1 === pagination.page} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={i === pagination.page}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        if (endPage < pagination.pages) {
            if (endPage < pagination.pages - 1) {
                items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
            }
            items.push(
                <Pagination.Item
                    key={pagination.pages}
                    active={pagination.pages === pagination.page}
                    onClick={() => handlePageChange(pagination.pages)}
                >
                    {pagination.pages}
                </Pagination.Item>
            );
        }

        items.push(
            <Pagination.Next
                key="next"
                disabled={pagination.page === pagination.pages}
                onClick={() => handlePageChange(pagination.page + 1)}
            />
        );

        return items;
    };

    const getSelectedStoreNames = () => {
        if (formData.store_ids.length === 0) return 'No stores selected';

        return formData.store_ids.map(storeId => {
            const store = stores.find(s => s.id === storeId);
            return store ? `${store.name} (${store.code || 'No Code'})` : storeId.substring(0, 12) + '...';
        }).join(', ');
    };

    if (loading && vendors.length === 0) return (
        <div className="text-center py-5">
            <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading vendors...</p>
        </div>
    );

    return (
        <div>
            <Row className="align-items-center mb-4">
                <Col>
                    <h1 className="mb-0">Vendors Management</h1>
                    <p className="text-muted mb-0">Manage product vendors and their stores</p>
                </Col>
                <Col xs="auto">
                    <Button
                        ref={createButtonRef}
                        variant="success"
                        onClick={() => {
                            resetForm();
                            setShowCreateModal(true);
                        }}
                        className="d-flex align-items-center"
                        disabled={actionLoading}
                    >
                        <FaPlus className="me-2" />
                        Create Vendor
                    </Button>
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-4">
                    {error}
                </Alert>
            )}

            {successMessage && (
                <Alert variant="success" dismissible onClose={() => setSuccessMessage('')} className="mb-4">
                    {successMessage}
                </Alert>
            )}

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <Row className="g-3">
                        <Col md={6}>
                            <div className="position-relative">
                                <FaSearch className="position-absolute top-50 start-3 translate-middle-y text-muted" />
                                <Form.Control
                                    type="text"
                                    placeholder="Search vendors by name, email, phone, website, or address..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="ps-5"
                                    disabled={actionLoading}
                                />
                            </div>
                        </Col>
                        <Col md={6} className="text-md-end d-flex align-items-center justify-content-end">
                            <div className="me-4">
                                <small className="text-muted">
                                    Total: <strong>{pagination.total}</strong> •
                                    Showing: <strong>{searchTerm ? filteredVendors.length : vendors.length}</strong> vendors
                                </small>
                            </div>
                            {searchTerm && (
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => setSearchTerm('')}
                                    disabled={actionLoading}
                                >
                                    Clear Search
                                </Button>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body className="p-0">
                    <Table responsive hover className="mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th>Vendor</th>
                                <th>Contact</th>
                                <th>Details</th>
                                <th>Associated Stores</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredVendors.length > 0 ? (
                                filteredVendors.map(vendor => (
                                    <tr key={vendor.id}>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <div
                                                    className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3"
                                                    style={{ width: '40px', height: '40px' }}
                                                >
                                                    <FaStore className="text-white" />
                                                </div>
                                                <div>
                                                    <div className="fw-semibold">{vendor.name || 'N/A'}</div>
                                                    <small className="text-muted">
                                                        ID: {vendor.id?.substring(0, 12)}...
                                                    </small>
                                                    {vendor.description && (
                                                        <div className="mt-1">
                                                            <small className="text-muted">{vendor.description}</small>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="small">
                                                <div className="fw-medium">
                                                    <FaUser className="me-1" size={12} />
                                                    {vendor.email || '-'}
                                                </div>
                                                {vendor.phone && (
                                                    <div className="text-muted">
                                                        <FaPhone className="me-1" size={10} />
                                                        {vendor.phone}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="small">
                                                {vendor.website && (
                                                    <div className="mb-1">
                                                        <FaGlobe className="me-1" size={12} />
                                                        <a
                                                            href={vendor.website.startsWith('http') ? vendor.website : `https://${vendor.website}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-decoration-none"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            {vendor.website.length > 30 ? vendor.website.substring(0, 30) + '...' : vendor.website}
                                                        </a>
                                                    </div>
                                                )}
                                                {vendor.address && (
                                                    <div className="mb-1">
                                                        <FaMapMarkerAlt className="me-1 text-muted" size={10} />
                                                        <small className="text-muted">
                                                            {vendor.address}
                                                        </small>
                                                    </div>
                                                )}
                                                {!vendor.website && !vendor.address && (
                                                    <div className="text-muted">
                                                        <small>No additional details</small>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            {vendor.stores && vendor.stores.length > 0 ? (
                                                <div>
                                                    {vendor.stores.slice(0, 2).map(store => (
                                                        <Badge
                                                            key={store.id}
                                                            bg="info"
                                                            className="me-1 mb-1 cursor-pointer"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                viewStoreDetails(store);
                                                            }}
                                                        >
                                                            {store.name}
                                                        </Badge>
                                                    ))}
                                                    {vendor.stores.length > 2 && (
                                                        <Badge
                                                            bg="secondary"
                                                            className="cursor-pointer"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                viewStoreDetails(vendor.stores[0]);
                                                            }}
                                                        >
                                                            +{vendor.stores.length - 2} more
                                                        </Badge>
                                                    )}
                                                </div>
                                            ) : (
                                                <Badge bg="secondary">No stores</Badge>
                                            )}
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                {getStatusBadge(vendor.status)}
                                                {vendor.status && (
                                                    <Button
                                                        variant="link"
                                                        size="sm"
                                                        className="ms-2 p-0"
                                                        onClick={() => handleStatusChange(vendor.id, vendor.status)}
                                                        title={`Change to ${vendor.status === 'active' ? 'inactive' : 'active'}`}
                                                        disabled={actionLoading}
                                                    >
                                                        {vendor.status === 'active' ? (
                                                            <FaTrash className="text-danger" size={14} />
                                                        ) : (
                                                            <FaChartLine className="text-success" size={14} />
                                                        )}
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="small">
                                                <div>{formatDate(vendor.created_at)}</div>
                                                <div className="text-muted">Updated: {formatDate(vendor.updated_at)}</div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="d-flex align-items-center"
                                                    onClick={() => openEditModal(vendor)}
                                                    title="Edit Vendor"
                                                    disabled={actionLoading}
                                                >
                                                    <FaEdit className="me-1" />
                                                    Edit
                                                </Button>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    className="d-flex align-items-center"
                                                    onClick={() => openDeleteModal(vendor)}
                                                    title="Delete Vendor"
                                                    disabled={actionLoading}
                                                >
                                                    <FaTrash className="me-1" />
                                                    Delete
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="text-center py-5">
                                        <div className="text-muted">
                                            {searchTerm ? (
                                                <>
                                                    <FaSearch className="h3 mb-3" />
                                                    <p>No vendors found matching "{searchTerm}"</p>
                                                </>
                                            ) : (
                                                <>
                                                    <FaStore className="h3 mb-3" />
                                                    <p>No vendors found</p>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            {pagination.pages > 1 && (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="py-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <small className="text-muted">
                                    Page {pagination.page} of {pagination.pages} •
                                    Showing {vendors.length} of {pagination.total} vendors
                                </small>
                            </div>
                            <Pagination className="mb-0">
                                {renderPagination()}
                            </Pagination>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {vendors.length > 0 && (
                <Row className="mt-4 g-3">
                    <Col md={3}>
                        <Card className="border-0 shadow-sm bg-primary text-white">
                            <Card.Body className="text-center py-3">
                                <div className="h4 mb-1">{pagination.total}</div>
                                <small>Total Vendors</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm bg-success text-white">
                            <Card.Body className="text-center py-3">
                                <div className="h4 mb-1">
                                    {vendors.filter(v => v.status === 'active' || !v.status).length}
                                </div>
                                <small>Active Vendors</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm bg-info text-white">
                            <Card.Body className="text-center py-3">
                                <div className="h4 mb-1">
                                    {vendors.filter(v => v.stores && v.stores.length > 0).length}
                                </div>
                                <small>Vendors with Stores</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm bg-warning text-dark">
                            <Card.Body className="text-center py-3">
                                <div className="h4 mb-1">
                                    {vendors.filter(v => v.phone || v.website || v.address).length}
                                </div>
                                <small>Vendors with Contact Info</small>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Create Modal */}
            <FormModal
                show={showCreateModal}
                onHide={handleModalClose}
                title="Create New Vendor"
                onSubmit={handleCreate}
                submitText="Create Vendor"
                size="lg"
                loading={actionLoading}
            >
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Vendor Name *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                                placeholder="Enter vendor name"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Email *</Form.Label>
                            <Form.Control
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                required
                                placeholder="Enter email"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label>Password *</Form.Label>
                    <Form.Control
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        placeholder="Enter password"
                        minLength="6"
                        disabled={actionLoading}
                    />
                    <Form.Text className="text-muted">
                        Password must be at least 6 characters long
                    </Form.Text>
                </Form.Group>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Phone</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                placeholder="Enter phone number"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Website</Form.Label>
                            <Form.Control
                                type="url"
                                value={formData.website}
                                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                                placeholder="https://example.com"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Enter vendor description"
                        disabled={actionLoading}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Address</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={2}
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="Enter vendor address"
                        disabled={actionLoading}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Store Associations (Required - Multiple Selection)</Form.Label>
                    {storesLoading ? (
                        <div className="d-flex align-items-center">
                            <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                                <span className="visually-hidden">Loading stores...</span>
                            </div>
                            <small className="text-muted">Loading stores...</small>
                        </div>
                    ) : (
                        <>
                            <div className="d-flex justify-content-between mb-2">
                                <div>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={selectAllStores}
                                        disabled={actionLoading || stores.length === 0}
                                        className="me-2"
                                    >
                                        Select All
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={clearAllStores}
                                        disabled={actionLoading || stores.length === 0}
                                    >
                                        Clear All
                                    </Button>
                                </div>
                                <span className="text-muted small">
                                    Selected: {formData.store_ids.length} / {stores.length}
                                </span>
                            </div>
                            <div className="border rounded p-3" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                {stores.length > 0 ? (
                                    stores.map(store => (
                                        <Form.Check
                                            key={store.id}
                                            type="checkbox"
                                            id={`create-store-${store.id}`}
                                            label={
                                                <div className="d-flex flex-column">
                                                    <span className="fw-semibold">{store.name}</span>
                                                    <small className="text-muted">
                                                        Code: {store.code || 'No Code'} | {store.address}
                                                    </small>
                                                </div>
                                            }
                                            checked={formData.store_ids.includes(store.id)}
                                            onChange={() => handleStoreCheckboxChange(store.id)}
                                            disabled={actionLoading}
                                            className="mb-2 pb-2 border-bottom"
                                        />
                                    ))
                                ) : (
                                    <p className="text-muted text-center mb-0">No stores available</p>
                                )}
                            </div>
                            <Form.Text className="text-danger d-block mt-2">
                                * You must select at least one store
                            </Form.Text>
                            {formData.store_ids.length > 0 && (
                                <div className="mt-2 p-2 bg-light rounded">
                                    <strong>Selected Stores ({formData.store_ids.length}):</strong>
                                    <div className="mt-1 small">
                                        {getSelectedStoreNames()}
                                    </div>
                                    <div className="mt-2 small text-muted">
                                        <strong>Store IDs that will be submitted:</strong>
                                        <div className="mt-1 p-2 bg-white rounded">
                                            <code>{formData.store_ids.join(', ')}</code>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </Form.Group>
            </FormModal>

            {/* Edit Modal */}
            <FormModal
                show={showEditModal}
                onHide={handleModalClose}
                title={`Edit Vendor: ${selectedVendor?.name || ''}`}
                onSubmit={handleUpdate}
                submitText="Update Vendor"
                size="lg"
                loading={actionLoading}
            >
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Vendor Name *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                                placeholder="Enter vendor name"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Email *</Form.Label>
                            <Form.Control
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                required
                                placeholder="Enter email"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label>Password</Form.Label>
                    <Form.Control
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="Enter new password (leave blank to keep current)"
                        minLength="6"
                        disabled={actionLoading}
                    />
                    <Form.Text className="text-muted">
                        Leave password blank to keep current password. Minimum 6 characters if changing.
                    </Form.Text>
                </Form.Group>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Phone</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                placeholder="Enter phone number"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Website</Form.Label>
                            <Form.Control
                                type="url"
                                value={formData.website}
                                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                                placeholder="https://example.com"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Enter vendor description"
                        disabled={actionLoading}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Address</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={2}
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="Enter vendor address"
                        disabled={actionLoading}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Store Associations (Required - Multiple Selection)</Form.Label>
                    {storesLoading ? (
                        <div className="d-flex align-items-center">
                            <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                                <span className="visually-hidden">Loading stores...</span>
                            </div>
                            <small className="text-muted">Loading stores...</small>
                        </div>
                    ) : (
                        <>
                            <div className="d-flex justify-content-between mb-2">
                                <div>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={selectAllStores}
                                        disabled={actionLoading || stores.length === 0}
                                        className="me-2"
                                    >
                                        Select All
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={clearAllStores}
                                        disabled={actionLoading || stores.length === 0}
                                    >
                                        Clear All
                                    </Button>
                                </div>
                                <span className="text-muted small">
                                    Selected: {formData.store_ids.length} / {stores.length}
                                </span>
                            </div>
                            <div className="border rounded p-3" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                {stores.length > 0 ? (
                                    stores.map(store => (
                                        <Form.Check
                                            key={store.id}
                                            type="checkbox"
                                            id={`edit-store-${store.id}`}
                                            label={
                                                <div className="d-flex flex-column">
                                                    <span className="fw-semibold">{store.name}</span>
                                                    <small className="text-muted">
                                                        Code: {store.code || 'No Code'} | {store.address}
                                                    </small>
                                                </div>
                                            }
                                            checked={formData.store_ids.includes(store.id)}
                                            onChange={() => handleStoreCheckboxChange(store.id)}
                                            disabled={actionLoading}
                                            className="mb-2 pb-2 border-bottom"
                                        />
                                    ))
                                ) : (
                                    <p className="text-muted text-center mb-0">No stores available</p>
                                )}
                            </div>
                            <Form.Text className="text-danger d-block mt-2">
                                * You must select at least one store
                            </Form.Text>
                            {formData.store_ids.length > 0 && (
                                <div className="mt-2 p-2 bg-light rounded">
                                    <strong>Selected Stores ({formData.store_ids.length}):</strong>
                                    <div className="mt-1 small">
                                        {getSelectedStoreNames()}
                                    </div>
                                    <div className="mt-2 small text-muted">
                                        <strong>Store IDs that will be submitted:</strong>
                                        <div className="mt-1 p-2 bg-white rounded">
                                            <code>{formData.store_ids.join(', ')}</code>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </Form.Group>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                show={showDeleteModal}
                onHide={() => !actionLoading && setShowDeleteModal(false)}
                onConfirm={handleDelete}
                title="Delete Vendor"
                message={
                    <div>
                        <p>Are you sure you want to delete vendor <strong>"{selectedVendor?.name}"</strong>?</p>
                        <p className="text-danger mb-0">This action cannot be undone.</p>
                    </div>
                }
                confirmText={actionLoading ? "Deleting..." : "Delete"}
                confirmDisabled={actionLoading}
                variant="danger"
            />

            {/* Store Details Modal */}
            <Modal show={showStoreDetailsModal} onHide={() => setShowStoreDetailsModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaStore className="me-2 text-warning" />
                        Store Details: {selectedStore?.name || ''}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loadingStoreDetails ? (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2 text-muted">Loading store details...</p>
                        </div>
                    ) : (
                        <>
                            <Card className="border-0 shadow-sm mb-4">
                                <Card.Body>
                                    <div className="d-flex align-items-center mb-3">
                                        <div
                                            className="bg-warning rounded-circle d-flex align-items-center justify-content-center me-3"
                                            style={{ width: '60px', height: '60px' }}
                                        >
                                            <FaStore className="text-white" size={24} />
                                        </div>
                                        <div>
                                            <h4 className="mb-1">{selectedStore?.name}</h4>
                                            <p className="text-muted mb-0">Code: {selectedStore?.code || 'N/A'}</p>
                                        </div>
                                    </div>

                                    <Row>
                                        <Col md={6}>
                                            <h6 className="text-muted mb-2">Location Information</h6>
                                            <div className="mb-3">
                                                <div className="d-flex mb-2">
                                                    <FaMapMarkerAlt className="me-2 text-primary" size={16} />
                                                    <span>{selectedStore?.address || 'No address'}</span>
                                                </div>
                                                <div className="ms-4 text-muted small">
                                                    {selectedStore?.city && selectedStore?.state ? (
                                                        <div>{selectedStore.city}, {selectedStore.state} {selectedStore.postal_code}</div>
                                                    ) : (
                                                        <div>{selectedStore?.city || selectedStore?.state || selectedStore?.postal_code}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <h6 className="text-muted mb-2">Contact Information</h6>
                                            <div className="mb-3">
                                                {selectedStore?.phone && (
                                                    <div className="d-flex mb-2">
                                                        <FaPhone className="me-2 text-success" size={16} />
                                                        <span>{selectedStore.phone}</span>
                                                    </div>
                                                )}
                                                {selectedStore?.email && (
                                                    <div className="d-flex mb-2">
                                                        <FaUser className="me-2 text-info" size={16} />
                                                        <span>{selectedStore.email}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </Col>
                                    </Row>

                                    <hr />

                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <Badge bg={selectedStore?.is_active === 1 ? 'success' : 'secondary'} className="me-2">
                                                {selectedStore?.is_active === 1 ? 'Active' : 'Inactive'}
                                            </Badge>
                                            <Badge bg="info">
                                                {selectedStore?.vendors_count || 0} {selectedStore?.vendors_count === 1 ? 'Vendor' : 'Vendors'}
                                            </Badge>
                                        </div>
                                        <div className="text-muted small">
                                            <div>Created: {formatDate(selectedStore?.created_at)}</div>
                                            <div>Updated: {formatDate(selectedStore?.updated_at)}</div>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>

                            {selectedStore?.vendors && selectedStore.vendors.length > 0 && (
                                <>
                                    <h5 className="mb-3">Associated Vendors</h5>
                                    <div className="vendor-list">
                                        {selectedStore.vendors.map(vendor => (
                                            <Card key={vendor.id} className="mb-3 border-0 shadow-sm">
                                                <Card.Body className="d-flex align-items-center">
                                                    <div
                                                        className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3"
                                                        style={{ width: '50px', height: '50px' }}
                                                    >
                                                        <FaStore className="text-white" />
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <h6 className="mb-1">{vendor.name}</h6>
                                                        <div className="d-flex flex-wrap gap-3 small text-muted">
                                                            <span><FaUser className="me-1" size={12} /> {vendor.email}</span>
                                                            {vendor.phone && (
                                                                <span><FaPhone className="me-1" size={12} /> {vendor.phone}</span>
                                                            )}
                                                            {vendor.website && (
                                                                <span><FaGlobe className="me-1" size={12} /> {vendor.website}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <Badge bg={vendor.status === 'active' ? 'success' : 'secondary'}>
                                                        {vendor.status || 'active'}
                                                    </Badge>
                                                </Card.Body>
                                            </Card>
                                        ))}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowStoreDetailsModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Vendors;